/** Structured Terms & Conditions CMS config stored in `terms_conditions.content`. */

import {
  DEFAULT_AUCTION_SECTIONS,
  DEFAULT_CONSULTANTS_SECTIONS,
  DEFAULT_CONTRACT_MANUFACTURING_SECTIONS,
  DEFAULT_INFRASTRUCTURE_LEASING_SECTIONS,
  DEFAULT_LEGAL_SECTIONS,
  DEFAULT_LOGISTICS_SECTIONS,
  DEFAULT_PLATFORM_SECTIONS,
  DEFAULT_PROCURE_TO_PAY_SECTIONS,
  DEFAULT_QUALITY_ASSESSMENT_SECTIONS,
  parseTermsSectionsFromHtml,
} from "./termsPagePublic";

export const TERMS_SECTION_ICON_OPTIONS = [
  { value: "file", label: "Document" },
  { value: "user", label: "User / Eligibility" },
  { value: "gavel", label: "Gavel / Auction Rules" },
  { value: "industry", label: "Factory / Manufacturing" },
  { value: "truck", label: "Truck / Logistics" },
  { value: "shield", label: "Shield / Responsibilities" },
  { value: "desktop", label: "Desktop / Services" },
  { value: "card", label: "Card / Payments" },
  { value: "ban", label: "Ban / Prohibited" },
  { value: "warning", label: "Warning / Liability" },
  { value: "lock", label: "Lock / Changes" },
  { value: "envelope", label: "Envelope / Contact" },
];

const GENERIC_MODULE_SECTIONS = (moduleLabel) => [
  {
    id: "introduction",
    title: "Introduction",
    summary: `These terms govern use of the ${moduleLabel} module on Trocanet.`,
    body: `<p>These terms govern use of the ${moduleLabel} module on Trocanet. By using this module you agree to these terms in addition to the Overall Platform Terms.</p>`,
    icon: "file",
  },
  {
    id: "scope-of-use",
    title: "Scope of Use",
    summary: `Describe how the ${moduleLabel} module may be used and any module-specific rules.`,
    body: `<p>Users must use the ${moduleLabel} module only for legitimate business purposes and in compliance with applicable laws and Trocanet policies.</p>`,
    icon: "shield",
  },
  {
    id: "user-obligations",
    title: "User Obligations",
    summary: "Users must provide accurate information and keep account credentials secure.",
    body: `<p>Users must provide accurate information, keep account credentials secure, and not misuse the module or attempt to circumvent platform processes.</p>`,
    icon: "user",
  },
  {
    id: "contact-support",
    title: "Contact Support",
    summary: "Contact Trocanet support for questions about these module terms.",
    body: `<p>For questions about these terms, please contact Trocanet support through the <a href="/contact">Contact</a> page or <a href="mailto:support@trocanet.com">support@trocanet.com</a>.</p>`,
    icon: "envelope",
  },
];

function isPlainObject(value) {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function slugify(value, fallback) {
  const slug = String(value || "")
    .toLowerCase()
    .replace(/&amp;/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return slug || fallback;
}

function cloneSections(sections) {
  return (sections || []).map((s) => ({ ...s, visible: s.visible !== false }));
}

const GENERIC_SECTION_IDS = new Set([
  "introduction",
  "scope-of-use",
  "user-obligations",
  "contact-support",
]);

function isLegacyGenericConfig(sections) {
  if (!Array.isArray(sections) || sections.length !== GENERIC_SECTION_IDS.size) return false;
  return sections.every((section) => GENERIC_SECTION_IDS.has(slugify(section?.id || section?.title, "")));
}

export function getDefaultTermsSections(moduleName = "PLATFORM", moduleLabel = "Module") {
  if (moduleName === "PLATFORM") {
    return cloneSections(DEFAULT_PLATFORM_SECTIONS.map((s) => ({ ...s, visible: true })));
  }
  if (moduleName === "AUCTION") {
    return cloneSections(DEFAULT_AUCTION_SECTIONS.map((s) => ({ ...s, visible: true })));
  }
  if (moduleName === "INFRASTRUCTURE_LEASING") {
    return cloneSections(
      DEFAULT_INFRASTRUCTURE_LEASING_SECTIONS.map((s) => ({ ...s, visible: true })),
    );
  }
  if (moduleName === "CONTRACT_MANUFACTURING") {
    return cloneSections(
      DEFAULT_CONTRACT_MANUFACTURING_SECTIONS.map((s) => ({ ...s, visible: true })),
    );
  }
  if (moduleName === "PROCURE_TO_PAY") {
    return cloneSections(DEFAULT_PROCURE_TO_PAY_SECTIONS.map((s) => ({ ...s, visible: true })));
  }
  if (moduleName === "QUALITY_ASSESSMENT") {
    return cloneSections(
      DEFAULT_QUALITY_ASSESSMENT_SECTIONS.map((s) => ({ ...s, visible: true })),
    );
  }
  if (moduleName === "CONSULTANTS") {
    return cloneSections(DEFAULT_CONSULTANTS_SECTIONS.map((s) => ({ ...s, visible: true })));
  }
  if (moduleName === "LEGAL") {
    return cloneSections(DEFAULT_LEGAL_SECTIONS.map((s) => ({ ...s, visible: true })));
  }
  if (moduleName === "LOGISTICS") {
    return cloneSections(DEFAULT_LOGISTICS_SECTIONS.map((s) => ({ ...s, visible: true })));
  }
  return cloneSections(GENERIC_MODULE_SECTIONS(moduleLabel).map((s) => ({ ...s, visible: true })));
}

export function createEmptyTermsSection(index = 0) {
  const n = index + 1;
  return {
    id: `section-${Date.now()}-${n}`,
    title: `Section ${n}`,
    summary: "",
    body: "<p></p>",
    icon: "file",
    visible: true,
  };
}

function normalizeSection(row, index, fallbackIcon = "file") {
  const allowedIcons = new Set(TERMS_SECTION_ICON_OPTIONS.map((o) => o.value));
  const title = String(row?.title ?? "").trim() || `Section ${index + 1}`;
  const iconValue = String(row?.icon ?? fallbackIcon).trim();
  return {
    id: slugify(row?.id || title, `section-${index + 1}`),
    title,
    summary: String(row?.summary ?? "").trim(),
    body: String(row?.body ?? "").trim() || "<p></p>",
    icon: allowedIcons.has(iconValue) ? iconValue : fallbackIcon,
    visible: row?.visible !== false,
  };
}

function normalizeSections(incoming, defaults) {
  if (!Array.isArray(incoming) || !incoming.length) {
    return cloneSections(defaults);
  }
  return incoming
    .filter((item) => isPlainObject(item))
    .map((item, index) => normalizeSection(item, index, defaults[index]?.icon || "file"));
}

export function getDefaultTermsCmsConfig(moduleName = "PLATFORM", moduleLabel = "Module") {
  return {
    schemaVersion: 2,
    moduleName,
    sections: getDefaultTermsSections(moduleName, moduleLabel),
  };
}

/**
 * Parse stored content into structured CMS config.
 * Accepts JSON schema v2 or legacy HTML (converted via heading split / defaults).
 */
export function parseTermsCmsConfig(content, { moduleName = "PLATFORM", moduleLabel = "Module" } = {}) {
  const defaults = getDefaultTermsCmsConfig(moduleName, moduleLabel);

  if (!content || typeof content !== "string") {
    return {
      ...defaults,
      sections: cloneSections(defaults.sections),
    };
  }

  const trimmed = content.trim();
  if (trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (isPlainObject(parsed) && Number(parsed.schemaVersion) === 2) {
        const storedModuleName = String(parsed.moduleName || "").trim().toUpperCase();
        const belongsToAnotherModule = storedModuleName && storedModuleName !== moduleName;
        const isGenericModuleSeed = moduleName !== "PLATFORM" && isLegacyGenericConfig(parsed.sections);

        if (belongsToAnotherModule || isGenericModuleSeed) {
          return {
            ...defaults,
            sections: cloneSections(defaults.sections),
          };
        }

        return {
          schemaVersion: 2,
          moduleName,
          sections: normalizeSections(parsed.sections, defaults.sections),
        };
      }
    } catch {
      // fall through to HTML path
    }
  }

  const fromHtml = parseTermsSectionsFromHtml(content, { moduleName });
  if (fromHtml.length) {
    return {
      schemaVersion: 2,
      sections: fromHtml.map((s, index) =>
        normalizeSection({ ...s, visible: true }, index, s.icon || "file"),
      ),
    };
  }

  return {
    ...defaults,
    sections: cloneSections(defaults.sections),
  };
}

export function serializeTermsCmsConfig(config, { moduleName = "PLATFORM", moduleLabel = "Module" } = {}) {
  const defaults = getDefaultTermsCmsConfig(moduleName, moduleLabel);
  const source = isPlainObject(config) ? config : defaults;
  const sections = normalizeSections(source.sections, defaults.sections);
  return JSON.stringify(
    {
      schemaVersion: 2,
      moduleName,
      sections,
    },
    null,
    2,
  );
}

/** Flatten structured config to HTML for acceptance modal / legacy preview. */
export function termsConfigToHtml(config) {
  const sections = Array.isArray(config?.sections) ? config.sections : [];
  const visible = sections.filter((s) => s.visible !== false);
  if (!visible.length) return "<p>Terms content is not available.</p>";
  return visible
    .map(
      (section, index) =>
        `<h2>${index + 1}. ${escapeHtml(section.title)}</h2>${section.summary ? `<p><em>${escapeHtml(section.summary)}</em></p>` : ""}${section.body || ""}`,
    )
    .join("\n");
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Public / preview: get visible accordion sections from stored content. */
export function buildTermsSectionsFromContent(
  content,
  { moduleName = "PLATFORM", moduleLabel = "Module" } = {},
) {
  const config = parseTermsCmsConfig(content, { moduleName, moduleLabel });
  return (config.sections || []).filter((s) => s.visible !== false);
}

/** Normalize any stored content to HTML for acceptance modal. */
export function termsContentToDisplayHtml(content, opts = {}) {
  const trimmed = String(content || "").trim();
  if (!trimmed) return "<p>Terms content is not available.</p>";
  if (trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (isPlainObject(parsed) && Number(parsed.schemaVersion) === 2) {
        return termsConfigToHtml(parseTermsCmsConfig(trimmed, opts));
      }
    } catch {
      // fall through
    }
  }
  return trimmed;
}
